import React, { useEffect, useRef, useState } from "react";
import { getMyBusyWeeks, listTimetableRuns, getPublishedRuns, getMyStaff, listClasses, listSubjects } from "./happyschoolApi";
import HappySchoolPendingEdits from "./HappySchoolPendingEdits";
import HappySchoolTimetableVersion from "./HappySchoolTimetableVersion";
import HappySchoolAllClassesView from "./HappySchoolAllClassesView";

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString("vi-VN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "");
const TIENGANH_SUBJECT_ID = "-OzGavPNsRqnKAKOETqK";

// Đúng 6 mục như cây quản lý TKB của admin (KHÔNG có nút quản trị nào), cộng
// thêm 1 mục riêng "Phân công chuyên môn" — không gắn với bản TKB nào cả.
const TABS = [
  { key: "teacher", mode: "teacher", icon: "🧑‍🏫", label: "TKB Giáo viên" },
  { key: "chamcong", mode: "chamcong", icon: "🧮", label: "Chấm công GV Văn hoá" },
  { key: "studentOnly", mode: "studentOnly", icon: "📖", label: "TKB Riêng cho Học sinh" },
  { key: "vh", mode: "vh", icon: "🏫", label: "TKB Toàn trường VH" },
  { key: "vhnghe", mode: "vhnghe", icon: "🏭", label: "TKB Toàn trường VH-Nghề" },
  { key: "teacherGridSang", mode: "teacherGridSang", icon: "🌤️", label: "TKB GV Buổi Sáng" },
  { key: "teacherGridChieu", mode: "teacherGridChieu", icon: "🌆", label: "TKB GV Buổi Chiều" },
  { key: "student", mode: "student", icon: "🎒", label: "TKB cho GV & Học sinh" },
  { key: "studentAll", mode: "studentAll", icon: "🎒", label: "TKB cho GV & Học sinh — Toàn trường" }, // (24/10/2026) có đổi tiết: GV bấm vào tiết của mình
  { key: "assignment", mode: null, icon: "📋", label: "Phân công chuyên môn" },
  { key: "pendingEdits", mode: null, icon: "🔔", label: "Xác nhận sửa TKB" }, // (24/10/2026) GVCN / GV bộ môn xác nhận các sửa đổi TKB liên quan tới mình
];

// "Phân công chuyên môn" của 1 GV — quét toàn bộ lớp, lọc môn mà GV này là
// GV được PCCM (GV chính) — đã bỏ GV dự phòng. KHÔNG phụ thuộc tuần/bản TKB nào.
const MyAssignment = ({ schoolId, staffId, staffKnown, onNeedVerify }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!schoolId || !staffId) return;
    setRows(null);
    setError("");
    Promise.all([listClasses(schoolId), listSubjects(schoolId)])
      .then(([classes, subjects]) => {
        const subjectName = Object.fromEntries((subjects || []).map((s) => [s.id, s.name]));
        const list = [];
        (classes || []).forEach((c) => {
          (c.subjects || []).forEach((s) => {
            const isMain = s.assignedTeacherId === staffId;
            if (isMain) {
              list.push({
                className: c.name,
                subjectName: subjectName[s.subjectId] || s.subjectId,
                role: "GV chính",
                periodsPerWeek: s.periodsPerWeek || 0,
              });
            }
          });
        });
        list.sort((a, b) => a.className.localeCompare(b.className, "vi", { numeric: true }) || a.subjectName.localeCompare(b.subjectName, "vi"));
        setRows(list);
      })
      .catch((e) => setError(e.message));
  }, [schoolId, staffId]);

  if (!staffKnown) {
    return (
      <div style={{ padding: 16, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e" }}>
        ⚠ Chưa xác định được đúng hồ sơ giáo viên của bạn nên không thể hiện đúng phân công chuyên môn.
        Hãy <b>Xác thực thông tin</b> trước.
        {onNeedVerify && (
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={onNeedVerify} style={verifyBtn}>🔎 Xác thực thông tin ngay</button>
          </div>
        )}
      </div>
    );
  }
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (rows === null) return <p>Đang tải...</p>;
  if (rows.length === 0) return <p style={{ color: "#6b7280" }}>Bạn chưa được phân công dạy lớp nào.</p>;

  const totalPeriods = rows.reduce((s, r) => s + r.periodsPerWeek, 0);

  return (
    <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
        <thead>
          <tr>
            <th style={th}>Lớp</th>
            <th style={th}>Môn học</th>
            <th style={th}>Vai trò</th>
            <th style={th}>Số tiết/tuần</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...td, fontWeight: 600 }}>{r.className}</td>
              <td style={td}>{r.subjectName}</td>
              <td style={{ ...td, color: r.role === "GV chính" ? "#0ea5a3" : "#9ca3af" }}>{r.role}</td>
              <td style={td}>{r.periodsPerWeek}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} style={{ ...td, fontWeight: 700, background: "#f9fafb", textAlign: "right" }}>Tổng</td>
            <td style={{ ...td, fontWeight: 700, background: "#f9fafb" }}>{totalPeriods}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// GV tự xem TKB — CHỈ tuần đang mở khai báo bận, lấy đúng Bản mới nhất đã
// xếp xong. Không có nút quản trị (Chạy xếp TKB / Tạo tuần / Đóng-mở khai báo).
const HappySchoolMyTimetable = ({ schoolId, onNeedVerify, baoGiangOnly = false }) => {
  const [weeks, setWeeks] = useState([]);
  const [weekId, setWeekId] = useState("");
  const [runId, setRunId] = useState("");
  const [runLabel, setRunLabel] = useState(""); // vd "Bản 137 · 09:41 06-09" — GV biết đang xem đúng bản nào
  const [runDemo, setRunDemo] = useState(false); // (24/10/2026) bản đang xem là bản công bố DEMO (xem thử)
  const [myStaffId, setMyStaffId] = useState("");
  const [mySelf, setMySelf] = useState(null); // {id,name,employmentType} — GV ngoài khối GV THPT (vd GV Nghề ở phòng Hành chính) vẫn xem được TKB của mình
  const [staffKnown, setStaffKnown] = useState(false); // đã xác định được đúng staffId của người đang đăng nhập chưa
  const [isTiengAnhTeacher, setIsTiengAnhTeacher] = useState(false); // GV Tiếng anh -> ẩn hẳn tab "Phân công chuyên môn"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("teacher");
  // GV đã tự chọn tuần ở ô "Tuần TKB" -> kết quả "tuần công bố gần nhất" (tải chậm) đến muộn KHÔNG được ghi đè lựa chọn đó.
  const pickedWeekRef = useRef(false);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    setError("");
    Promise.all([getMyBusyWeeks(schoolId), getMyStaff(schoolId)])
      .then(([ws, me]) => {
        const sorted = (ws || []).slice().sort((a, b) => (b.startDate || "").localeCompare(a.startDate || ""));
        setWeeks(sorted);
        setMyStaffId((me && me.staffId) || "");
        setMySelf(me && me.staffId ? { id: me.staffId, name: me.name || "", employmentType: me.employmentType || "", department: me.department || "" } : null);
        setStaffKnown(!!(me && me.staffId));
        setIsTiengAnhTeacher(!!(me && (me.subjectIds || []).includes(TIENGANH_SUBJECT_ID)));
        if (!sorted.length) { setLoading(false); return; }
        // Mặc định KHÔNG chọn tuần mới nhất theo NGÀY — chọn tuần có bản TKB
        // ĐÃ CÔNG BỐ gần đây nhất (yêu cầu rõ: "cần lấy cái TKB được công bố
        // gần nhất"), vì tuần mới tạo có thể chưa xếp/công bố xong trong khi
        // 1 tuần trước đó đã có bản chính thức. Đọc thẳng bảng publishedRuns
        // (server ghi 1 lần lúc bấm "Công bố", xem publish() trong
        // timetable.js) thay vì tự quét list-runs từng tuần ở client — nhanh
        // hơn và không còn lỡ rớt về tuần chưa công bố khi tuần đó mới nhất
        // theo ngày. CHỈ xét trong đúng loại tuần đang xem (thường hoặc Báo
        // giảng, theo baoGiangOnly) — trước đây lấy bản công bố gần nhất
        // TOÀN TRƯỜNG nên tuần Báo giảng mới công bố có thể lấn cả sang thẻ
        // "Xem thời khoá biểu" thường. Không có bản nào công bố (đúng loại)
        // thì lùi về tuần mới nhất theo ngày (đúng loại) như cũ.
        const ownWeeks = sorted.filter((w) => !!w.excludeFromChamCong === baoGiangOnly);
        getPublishedRuns(schoolId)
          .then((published) => {
            const ownWeekIds = new Set(ownWeeks.map((w) => w.id));
            const best = (published || []).find((p) => ownWeekIds.has(p.weekId)); // da sap publishedAt giam dan o server
            if (!pickedWeekRef.current) setWeekId(best ? best.weekId : (ownWeeks[0] ? ownWeeks[0].id : ""));
            if (!ownWeeks.length) setLoading(false);
          })
          .catch(() => { if (!pickedWeekRef.current) setWeekId(ownWeeks[0] ? ownWeeks[0].id : ""); });
      })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [schoolId, baoGiangOnly]);

  useEffect(() => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    setError("");
    setRunId("");
    setRunLabel("");
    setRunDemo(false);
    // Đổi tuần liên tiếp: phản hồi của tuần CŨ (đến muộn) không được ghi đè bản của tuần MỚI chọn.
    let stale = false;
    listTimetableRuns(schoolId, weekId)
      .then((runs) => {
        if (stale) return;
        // CHỈ lấy bản đã được owner/admin "Công bố" (published) — không tự
        // động lấy bản mới nhất, tránh GV thấy các bản đang thử nghiệm.
        const list = runs || [];
        const idx = list.findIndex((r) => r.status === "done" && r.published);
        if (idx >= 0) {
          const publishedRun = list[idx];
          setRunId(publishedRun.id);
          setRunDemo(!!publishedRun.demo);
          // Cùng cách đánh số "Bản N" như trang quản lý của admin (list đã
          // sắp mới nhất trước — N = length - vị trí trong list).
          setRunLabel(`Bản ${list.length - idx} · ${fmtTime(publishedRun.finishedAt || publishedRun.createdAt)}`);
        }
      })
      .catch((e) => { if (!stale) setError(e.message); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [schoolId, weekId]);

  // Tách hẳn tuần Lịch báo giảng (excludeFromChamCong) khỏi TKB thường ngay
  // từ đầu vào — component này được mount 2 nơi khác nhau ở portal (thẻ "Xem
  // thời khoá biểu" và thẻ riêng "Lịch báo giảng"), quyết định bởi prop
  // baoGiangOnly, áp dụng cho MỌI tab con bên trong (TKB Giáo viên, Chấm
  // công, TKB Toàn trường...) chứ không chỉ riêng 1 tab.
  const filteredWeeks = weeks.filter((w) => !!w.excludeFromChamCong === baoGiangOnly);

  useEffect(() => {
    if (!filteredWeeks.length) return;
    if (!filteredWeeks.some((w) => w.id === weekId)) {
      setWeekId(filteredWeeks[0].id);
    }
    // eslint-disable-next-line
  }, [weeks, baoGiangOnly]);

  const week = filteredWeeks.find((w) => w.id === weekId);
  // GV Tiếng anh: ẩn hẳn tab "Phân công chuyên môn" (yêu cầu rõ) — các GV
  // môn khác vẫn thấy đầy đủ như cũ.
  const visibleTabs = isTiengAnhTeacher ? TABS.filter((t) => t.key !== "assignment") : TABS;
  const activeTab = visibleTabs.find((t) => t.key === tab) || visibleTabs[0];
  const needsWeek = activeTab.mode !== null; // các tab TKB cần có tuần + bản; "Phân công chuyên môn" thì không

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Xem thời khoá biểu — mặc định hiện bản đã công bố gần đây nhất (tuần nào cũng được), đổi sang tuần khác ở ô chọn bên dưới.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {visibleTabs.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} style={tab === t.key ? tabActive : tabBtn}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "pendingEdits" ? (
        <HappySchoolPendingEdits initialStatus="applied" />
      ) : tab === "assignment" ? (
        <MyAssignment schoolId={schoolId} staffId={myStaffId} staffKnown={staffKnown} onNeedVerify={onNeedVerify} />
      ) : filteredWeeks.length === 0 && !loading ? (
        // Có lỗi thật (vd tài khoản không đủ quyền gọi API) thì phải hiện rõ
        // lỗi đó — trước đây luôn hiện "Chưa có tuần..." dù lỗi là gì, khiến
        // lỗi phân quyền (403) bị nhầm thành trường chưa mở tuần nào, không
        // ai phát hiện ra được (đã gặp thực tế: role "schedule_viewer" bị
        // /me/busy-weeks chặn nhưng UI im lặng, tưởng nhầm là chưa có tuần).
        error ? (
          <p style={{ color: "#dc2626" }}>{error}</p>
        ) : (
          <p style={{ color: "#b45309" }}>
            {baoGiangOnly ? "Chưa có tuần Lịch báo giảng nào được tạo." : "Chưa có tuần thời khoá biểu nào được tạo. Chờ nhà trường mở khai báo."}
          </p>
        )
      ) : (
        <>
          {(filteredWeeks.length > 1 || runLabel) && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
              {filteredWeeks.length > 1 && (
                <>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#4b5563" }}>Tuần TKB:</span>
                  <select value={weekId} onChange={(e) => { pickedWeekRef.current = true; setWeekId(e.target.value); }} style={{ padding: 8, minWidth: 240 }}>
                    {filteredWeeks.map((w) => (
                      <option key={w.id} value={w.id}>
                        {(w.startDate || "?") + " → " + (w.endDate || "?")}{(w.khois && w.khois.length) ? ` (Khối ${w.khois.join(",")})` : ""}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {week?.excludeFromChamCong && (
                <span style={{ fontSize: 12, fontWeight: 600, color: "#9a3412", background: "#ffedd5", border: "1px solid #fdba74", borderRadius: 999, padding: "3px 10px" }}
                  title="Tuần này là lịch báo giảng, không tính chấm công theo TKB thường">
                  Loại: Lịch báo giảng
                </span>
              )}
              {activeTab.mode !== null && runLabel && (
                <span style={{ fontSize: 12, color: "#6b7280", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 999, padding: "3px 10px" }}
                  title="Bản thời khoá biểu đã được công bố đang hiển thị">
                  ▾ 📄 {runLabel}
                </span>
              )}
            </div>
          )}

          {error && <p style={{ color: "#dc2626" }}>{error}</p>}
          {!loading && runId && runDemo && activeTab.mode !== null && (
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, color: "#92400e", fontSize: 13 }}>
              🧪 <b>Bản DEMO (xem thử)</b> — thời khoá biểu này chưa chính thức, có thể còn thay đổi và <b>không tính chấm công</b>.
            </div>
          )}
          {loading ? (
            <p>Đang tải...</p>
          ) : !runId ? (
            <p style={{ color: "#b45309" }}>Tuần này chưa có bản thời khoá biểu nào được công bố.</p>
          ) : (activeTab.mode === "teacher" || activeTab.mode === "chamcong") && !staffKnown ? (
            <div style={{ padding: 16, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e" }}>
              ⚠ Chưa xác định được đúng hồ sơ giáo viên của bạn nên không thể hiện đúng dữ liệu cá nhân (TKB / chấm công).
              Hãy <b>Xác thực thông tin</b> trước, hoặc xem tạm các mục khác.
              {onNeedVerify && (
                <div style={{ marginTop: 10 }}>
                  <button type="button" onClick={onNeedVerify} style={verifyBtn}>🔎 Xác thực thông tin ngay</button>
                </div>
              )}
            </div>
          ) : activeTab.mode === "studentAll" ? (
            <HappySchoolAllClassesView key={runId} runId={runId} weekId={weekId} />
          ) : (
            <HappySchoolTimetableVersion
              runId={runId}
              weekId={weekId}
              mode={activeTab.mode}
              khois={week ? week.khois : []}
              startDate={week ? week.startDate : ""}
              defaultTeacherId={myStaffId}
              selfTeacher={mySelf}
              lockTeacher={activeTab.mode === "teacher" || activeTab.mode === "chamcong"}
            />
          )}
        </>
      )}
    </div>
  );
};

const th = { border: "1px solid #e5e7eb", padding: "6px 10px", background: "#f9fafb", textAlign: "left" };
const td = { border: "1px solid #e5e7eb", padding: "6px 10px" };
const tabBtn = { padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const tabActive = { ...tabBtn, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };
const verifyBtn = { padding: "8px 16px", background: "#b45309", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 };

export default HappySchoolMyTimetable;
